import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Alert, Image } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import SearchBar from '../../../components/SearchBar';
import DataTable, { DataTableColumn } from '../../../components/data-table/DataTable';
import { TherapistListDto } from '../../../../../shared/dtos/therapist.dto';
import TherapistDetailsModal from './TherapistDetailsModal';
import { formatDateTime } from '../../../util/date-utils';

function buildQueryParameters(searchTerm: string): URLSearchParams {
  const queryParameters: URLSearchParams = new URLSearchParams();
  const trimmedSearchTerm: string = searchTerm.trim();

  if (trimmedSearchTerm) {
    queryParameters.set('searchTerm', trimmedSearchTerm);
  }

  return queryParameters;
}

function getTherapistName(therapist: TherapistListDto): string {
  const fullName: string = `${therapist.firstName} ${therapist.lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return therapist.email;
}

const Therapists = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [therapists, setTherapists] = useState<TherapistListDto[]>([]);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTherapistId, setSelectedTherapistId] = useState<string | null>(null);

  const fetchTherapists: (searchTerm: string) => Promise<void> = useCallback(async (searchTerm: string): Promise<void> => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response: TherapistListDto[] = await authFetch('/api/v1/admin/therapists', {
        method: 'GET',
        queryParameters: buildQueryParameters(searchTerm),
      });

      setTherapists(response ?? []);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  },
  [authFetch],
  );

  useEffect(() => {
    void fetchTherapists(appliedSearchTerm);
  }, [appliedSearchTerm, fetchTherapists]);

  const handleSearch: (value: string) => void = useCallback((value: string): void => {
    setAppliedSearchTerm(value.trim());
  }, []);

  function handleOpenDetailsModal(therapist: TherapistListDto): void {
    setSelectedTherapistId(therapist.id);
    setShowDetailsModal(true);
  }

  function handleCloseDetailsModal(): void {
    setShowDetailsModal(false);
    setSelectedTherapistId(null);
  }

  const columns: DataTableColumn<TherapistListDto>[] = [
    {
      key: 'therapist',
      header: i18next.t('therapists.table.therapist'),
      cell: (therapist: TherapistListDto) => (
        <div className="d-flex align-items-center gap-3">
          <Image
            src={therapist.profilePic ?? '../default_profile.png'}
            roundedCircle
            className="border flex-shrink-0"
            width={40}
            height={40}
            alt={getTherapistName(therapist)}
          />

          <div>
            <div className="fw-semibold">{getTherapistName(therapist)}</div>
            <div className="text-muted small">{therapist.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'plannedAppointments',
      header: i18next.t('therapists.table.plannedAppointments'),
      align: 'center',
      cell: (therapist: TherapistListDto) => therapist.plannedAppointmentsCount,
    },
    {
      key: 'doneAppointments',
      header: i18next.t('therapists.table.doneAppointments'),
      align: 'center',
      cell: (therapist: TherapistListDto) => therapist.doneAppointmentsCount,
    },
    {
      key: 'sessions',
      header: i18next.t('therapists.table.sessions'),
      align: 'center',
      cell: (therapist: TherapistListDto) => therapist.sessionsCount,
    },
    {
      key: 'patients',
      header: i18next.t('therapists.table.patients'),
      align: 'center',
      cell: (therapist: TherapistListDto) => therapist.patientsCount,
    },
    {
      key: 'lastLoginAt',
      header: i18next.t('therapists.table.lastLoginAt'),
      cell: (therapist: TherapistListDto) => <span className="text-nowrap">{formatDateTime(therapist.lastLoginAt)}</span>,
    },
  ];

  return (
    <div className="d-flex flex-column gap-3">
      {hasError && (
        <Alert
          variant="danger"
          className="mb-0">
          {i18next.t('therapists.error')}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={therapists}
        rowKey={(therapist: TherapistListDto) => therapist.id}
        onRowClick={handleOpenDetailsModal}
        isLoading={isLoading}
        emptyText={i18next.t('therapists.empty')}
        toolbar={(
          <SearchBar
            onSearch={handleSearch}
            debounceMs={300}
            maxWidth={400}
          />
        )}
      />

      {selectedTherapistId && (
        <TherapistDetailsModal
          show={showDetailsModal}
          therapistId={selectedTherapistId}
          onClose={handleCloseDetailsModal}
        />
      )}
    </div>
  );
};

export default Therapists;
